import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Truck, Shield, Clock } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { CATEGORIES } from '@/data/products';
import { ProductCard } from '@/components/ProductCard';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';
import { useProducts } from '@/hooks/useSupabaseData';

export default function Home() {
  const { products, loading } = useProducts();
  const featuredProducts = products.filter((p) => p.featured).slice(0, 4);

  return (
    <div className="min-h-screen">
      {/* Hero Section - مصغر */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_BANNER_1}
            alt="Shopping lifestyle"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <motion.div
          className="relative z-10 container mx-auto px-4 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            مرحباً بكم في اليامن
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            وجهتك المميزة للتسوق
          </p>
          <Link to={ROUTE_PATHS.PRODUCTS}>
            <Button size="lg" className="text-lg px-8 py-6">
              <ShoppingBag className="ml-2 h-5 w-5" />
              تسوق الآن
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* أقسام المنتجات */}
      <section className="py-16 bg-background" dir="rtl">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl font-bold mb-4">تسوق حسب الفئة</h2>
            <p className="text-muted-foreground text-lg">اكتشف مجموعتنا المتنوعة</p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {CATEGORIES.map((category, index) => (
              <motion.div
                key={category.id}
                variants={staggerItem}
                whileHover={{ scale: 1.05, y: -8 }}
                transition={springPresets.bouncy}
              >
                <Link to={`${ROUTE_PATHS.PRODUCTS}?category=${category.id}`}>
                  <Card className="overflow-hidden cursor-pointer group border-2 hover:border-primary transition-all duration-300 shadow-lg hover:shadow-2xl">
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="text-xl md:text-2xl font-bold text-center drop-shadow-lg">
                          {category.name}
                        </h3>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* المنتجات المميزة */}
      <section className="py-16 bg-muted/30" dir="rtl">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl font-bold mb-4">المنتجات المميزة</h2>
            <p className="text-muted-foreground text-lg">اكتشف مجموعتنا المختارة بعناية</p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {loading ? (
              // Loading skeleton
              Array.from({ length: 4 }).map((_, index) => (
                <motion.div key={index} variants={staggerItem}>
                  <div className="bg-muted animate-pulse rounded-lg h-96" />
                </motion.div>
              ))
            ) : featuredProducts.length > 0 ? (
              featuredProducts.map((product, index) => (
                <motion.div 
                  key={product.id} 
                  variants={staggerItem}
                  whileHover={{ y: -8 }}
                  transition={springPresets.bouncy}
                >
                  <ProductCard product={product} featured />
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-muted-foreground">لا توجد منتجات مميزة حالياً</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* المميزات */}
      <section className="py-16" dir="rtl">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl font-bold mb-4">لماذا تختارنا؟</h2>
            <p className="text-muted-foreground text-lg">نقدم لك أفضل تجربة تسوق</p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={springPresets.bouncy}
            >
              <Card className="text-center p-6 md:p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <CardContent className="pt-6">
                  <motion.div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Truck className="h-8 w-8 text-primary" />
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-2">شحن مجاني</h3>
                  <p className="text-muted-foreground">للطلبات فوق 500 ج.م</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={springPresets.bouncy}
            >
              <Card className="text-center p-6 md:p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <CardContent className="pt-6">
                  <motion.div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Shield className="h-8 w-8 text-primary" />
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-2">دفع آمن</h3>
                  <p className="text-muted-foreground">معاملات آمنة 100%</p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div 
              variants={staggerItem}
              whileHover={{ scale: 1.05, y: -5 }}
              transition={springPresets.bouncy}
            >
              <Card className="text-center p-6 md:p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <CardContent className="pt-6">
                  <motion.div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <Clock className="h-8 w-8 text-primary" />
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-2">دعم 24/7</h3>
                  <p className="text-muted-foreground">دائماً هنا لمساعدتك</p>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}